import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import firebase from '../firebaseConfig';
import flowTrackerService from '../services/flowTrackerService';
import DashBoardNavBar from './DashboardNavbar';
import NoSubscription from './NoSubscription';
import FocusTimer from './FocusTimer';
import PersonalNotes from './PersonalNotes';
import DashboardSettings from './DashboardSettings';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    Tooltip
} from 'recharts';
import '../styles/OverviewDashboard.css';

// Configuration modulaire du dashboard
const DASHBOARD_CONFIG = {
    sections: {
        statsCards: {
            enabled: true,
            cards: {
                flowTime: { enabled: true, order: 1 },
                averageScore: { enabled: true, order: 2 },
                goals: { enabled: true, order: 3 },
                experiences: { enabled: true, order: 4 }
            }
        },
        focusTimer: {
            enabled: true,
            order: 2
        },
        charts: {
            enabled: true,
            order: 4,
            charts: {
                flowEvolution: { enabled: true, order: 1 },
                categoryDistribution: { enabled: true, order: 2 }
            }
        },
        quickAccess: {
            enabled: true,
            order: 5,
            buttons: {
                dashboard: { enabled: true, order: 1 },
                flowtracker: { enabled: true, order: 2 },
                cycles: { enabled: true, order: 3 },
                experiences: { enabled: true, order: 4 },
                positiveAffirmations: { enabled: true, order: 5 },
                tutorials: { enabled: true, order: 6 },
                personalTraining: { enabled: true, order: 7 },
                feedback: { enabled: true, order: 8 }
            }
        },
        activities: {
            enabled: true,
            order: 3,
            components: {
                recentActivities: { enabled: true, order: 1 },
                upcomingDeadlines: { enabled: true, order: 2 }
            }
        }
    }
};

const OverviewDashboard = ({ user, config = DASHBOARD_CONFIG }) => {
    const [planValidity, setPlanValidity] = useState(0);
    const [userName, setUserName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [showSettings, setShowSettings] = useState(false);
    const [currentConfig, setCurrentConfig] = useState(config);
    const [dashboardData, setDashboardData] = useState({
        flowStats: {
            totalTime: '0h 0m',
            averageScore: 0,
            weeklyImprovement: 0
        },
        goals: {
            active: 0,
            completed: 0,
            total: 0
        },
        experiences: {
            total: 0,
            recent: []
        },
        flowData: [],
        categoryData: [],
        recentActivities: [],
        upcomingDeadlines: []
    });
    
    const navigate = useNavigate();

    // Navigation buttons avec icônes et dégradés professionnels
    const navigationButtons = [
        {
            key: 'dashboard',
            label: 'Flow State Habits',
            path: '/flow-state-habits',
            icon: 'fas fa-font-awesome',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#667eea'
        },
        {
            key: 'flowtracker',
            label: 'Flow Tracker',
            path: '/flowtracker',
            icon: 'fas fa-chart-line',
            gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: '#f093fb'
        },
        {
            key: 'cycles',
            label: 'Cycles',
            path: '/cycles',
            icon: 'fas fa-sync-alt',
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: '#4facfe'
        },
        {
            key: 'experiences',
            label: 'Experiences',
            path: '/experiences',
            icon: 'fas fa-star',
            gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: '#43e97b'
        },
        {
            key: 'positiveAffirmations',
            label: 'Positive Affirmations',
            path: '/PositiveAffirmations',
            icon: 'fas fa-heart',
            gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: '#fa709a'
        },
        {
            key: 'tutorials',
            label: 'Tutorials',
            path: '/Tutorials',
            icon: 'fas fa-graduation-cap',
            gradient: 'linear-gradient(135deg,rgb(132, 118, 155) 0%,rgb(62, 157, 239) 100%)',
            color: '#a8edea'
        },
        {
            key: 'personalTraining',
            label: 'Personal Training',
            path: '/personalTraining',
            icon: 'fas fa-dumbbell',
            gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            color: '#ffecd2'
        },
        {
            key: 'feedback',
            label: 'Feedback',
            path: '/feedback',
            icon: 'fas fa-comment-dots',
            gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
            color: '#a18cd1'
        }
    ];

    // Couleurs FlowTracker par catégorie
    const categoryColors = {
        Workflow: "#6373ff",
        "Creativity/Arts": "#6dce00",
        Creativity: "#6dce00",
        Sport: "#e7c008",
        Communication: "#f1232a",
        "Socializing/Communication/Public Speech": "#f1232a",
        Education: "#be21e8",
        Reading: "#4fe6f5",
        Other: "#b0aeae",
    };

    // Fonction pour obtenir la couleur d'une catégorie
    const getCategoryColor = (categoryName) => {
        // Vérification directe
        if (categoryColors[categoryName]) {
            return categoryColors[categoryName];
        }
        
        // Vérifications partielles pour les noms composés
        if (categoryName.includes('Creativity') || categoryName.includes('Arts')) {
            return categoryColors.Creativity;
        }
        if (categoryName.includes('Communication') || categoryName.includes('Socializing')) {
            return categoryColors.Communication;
        }
        if (categoryName.includes('Workflow')) {
            return categoryColors.Workflow;
        }
        if (categoryName.includes('Sport')) {
            return categoryColors.Sport;
        }
        if (categoryName.includes('Education')) {
            return categoryColors.Education;
        }
        if (categoryName.includes('Reading')) {
            return categoryColors.Reading;
        }
        
        // Couleur par défaut
        return categoryColors.Other;
    };

    // Fonction de conversion pour le format HH:MM comme dans FlowTracker
    const convertDecimalToHHMM = (decimal) => {
        const hours = Math.floor(decimal);
        const minutes = Math.round((decimal - hours) * 60);
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    useEffect(() => {
        const checkPlanValidity = async () => {
            if (user) {
                const userId = user.uid;
                const docRef = firebase.firestore().collection('users').doc(userId);
                const docSnapshot = await docRef.get();
                if (docSnapshot.exists) {
                    const data = docSnapshot.data();
                    setUserName(data.username || user.displayName || user.email);
                    
                    // Charger la configuration personnalisée si elle existe
                    if (data.dashboardConfig) {
                        setCurrentConfig(data.dashboardConfig);
                    }
                    
                    const endSubscriptionDate = data.endSubscriptionDate?.toDate();
                    if (endSubscriptionDate > new Date()) {
                        setPlanValidity(2);
                        await fetchDashboardData(data);
                    } else {
                        setPlanValidity(1);
                    }
                }
            } else {
                navigate('/login');
            }
            setIsLoading(false);
        };
        checkPlanValidity();
    }, [user, navigate]);

    const handleConfigChange = (newConfig) => {
        setCurrentConfig(newConfig);
    };

    const fetchDashboardData = async (userData) => {
        try {
            // Récupérer les données FlowTracker depuis la nouvelle collection
            let flowTrackerData = [];
            
            if (user) {
                try {
                    // Vérifier si les données ont été migrées vers la nouvelle collection
                    flowTrackerData = await flowTrackerService.getAllFlowTrackerEntries(user.uid);
                    
                    // Si aucune donnée n'est trouvée dans la nouvelle collection, utiliser les anciennes données
                    if (flowTrackerData.length === 0 && userData.FlowTrackerData) {
                        console.log("Using legacy FlowTrackerData from user document");
                        flowTrackerData = userData.FlowTrackerData.map(entry => ({
                            ...entry.data,
                            date: entry.date
                        }));
                    }
                } catch (error) {
                    console.error("Error fetching flow tracker data from new collection:", error);
                    // Fallback aux anciennes données en cas d'erreur
                    if (userData.FlowTrackerData) {
                        flowTrackerData = userData.FlowTrackerData.map(entry => ({
                            ...entry.data,
                            date: entry.date
                        }));
                    }
                }
            }
            
            // Trier les données par date
            flowTrackerData.sort((a, b) => {
                const dateA = a.date instanceof firebase.firestore.Timestamp ? a.date.toDate() : new Date(a.date);
                const dateB = b.date instanceof firebase.firestore.Timestamp ? b.date.toDate() : new Date(b.date);
                return dateA - dateB;
            });
            
            const last30Days = flowTrackerData.slice(-30);
            
            // Calcul des statistiques de flow avec comparaison hebdomadaire
            const flowStats = calculateFlowStats(last30Days, flowTrackerData);
            
            // Données pour le graphique d'évolution - 7 derniers jours
            const last7Days = last30Days.slice(-7);
            const flowChartData = last7Days.map(entry => {
                const entryDate = entry.date instanceof firebase.firestore.Timestamp ? 
                    entry.date.toDate() : new Date(entry.date);
                    
                return {
                    date: entryDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
                    score: parseFloat(entry.meanFlowQuality) || 0,
                    duration: convertTimeToHours(entry.grandTotal || '00:00')
                };
            });

            // Données pour le graphique de répartition par catégorie
            const categoryStats = calculateCategoryStats(flowTrackerData);
            
            // Données des expériences
            const experiences = userData.experiences || [];
            const recentExperiences = experiences
                .sort((a, b) => b.id - a.id)
                .slice(0, 3)
                .map(exp => ({
                    id: exp.id,
                    title: exp.title || 'Expérience sans titre',
                    date: new Date(exp.id).toLocaleDateString('fr-FR'),
                    icon: 'fas fa-star'
                }));

            // Recent activities simulation (adapt according to your data)
            const recentActivities = [
                {
                    id: 1,
                    title: 'Flow session completed',
                    time: '2 hours ago',
                    icon: 'fas fa-font-awesome'
                },
                {
                    id: 2,
                    title: 'New experience added',
                    time: 'Yesterday',
                    icon: 'fas fa-plus'
                },
                {
                    id: 3,
                    title: 'Goal completed',
                    time: '3 days ago',
                    icon: 'fas fa-check'
                }
            ];

            // Upcoming deadlines simulation
            const upcomingDeadlines = [
                {
                    id: 1,
                    title: 'Monthly goals review',
                    date: 'In 3 days',
                    progress: 75
                },
                {
                    id: 2,
                    title: 'Scheduled coaching session',
                    date: 'In 1 week',
                    progress: 0
                }
            ];

            // Calcul dynamique des Goals basé sur les 10 champs Goals_1
            const goalsData = userData.Goals_1 || {};
            const goalFields = [
                'globalView_1', 'intentions_1', 'in10x_1', 'experience_1',
                'globalView_2', 'intentions_2', 'globalView_3', 'intentions_3',
                'processGoals_4', 'values_5'
            ];
            
            const completedGoals = goalFields.filter(field => 
                goalsData[field] && goalsData[field].trim().length > 0
            ).length;

            setDashboardData({
                flowStats,
                goals: {
                    active: completedGoals,
                    completed: completedGoals,
                    total: 10
                },
                experiences: {
                    total: experiences.length,
                    recent: recentExperiences
                },
                flowData: flowChartData,
                categoryData: categoryStats,
                recentActivities,
                upcomingDeadlines
            });

        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const calculateFlowStats = (flowData, allFlowData = []) => {
        if (flowData.length === 0) {
            return {
                totalTime: '0h 0m',
                averageScore: 0,
                weeklyImprovement: 0
            };
        }

        let totalMinutes = 0;
        let totalScore = 0;
        let validEntries = 0;

        flowData.forEach(entry => {
            // Vérifier si les données sont dans le nouveau format ou l'ancien format
            const grandTotal = entry.grandTotal || (entry.data ? entry.data.grandTotal : '00:00');
            const meanFlowQuality = entry.meanFlowQuality || (entry.data ? entry.data.meanFlowQuality : 0);
            
            if (grandTotal && grandTotal !== '00:00') {
                const [hours, minutes] = grandTotal.split(':').map(Number);
                totalMinutes += (hours * 60) + minutes;
            }
            
            if (meanFlowQuality && meanFlowQuality > 0) {
                // Conversion explicite en nombre pour éviter la concaténation de chaînes
                const scoreValue = parseFloat(meanFlowQuality);
                if (!isNaN(scoreValue) && scoreValue > 0) {
                    totalScore += scoreValue;
                    validEntries++;
                }
            }
        });

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const averageScore = validEntries > 0 ? (totalScore / validEntries) : 0;

        // Calcul de l'amélioration hebdomadaire
        let weeklyImprovement = 0;
        if (allFlowData.length >= 14) {
            // Obtenir les 7 derniers jours (cette semaine)
            const thisWeek = allFlowData.slice(-7);
            // Obtenir les 7 jours précédents (semaine précédente)
            const lastWeek = allFlowData.slice(-14, -7);
            
            // Calculer la moyenne de cette semaine
            let thisWeekTotal = 0;
            let thisWeekValid = 0;
            thisWeek.forEach(entry => {
                const scoreValue = parseFloat(entry.data.meanFlowQuality);
                if (!isNaN(scoreValue) && scoreValue > 0) {
                    thisWeekTotal += scoreValue;
                    thisWeekValid++;
                }
            });
            const thisWeekAverage = thisWeekValid > 0 ? thisWeekTotal / thisWeekValid : 0;
            
            // Calculer la moyenne de la semaine précédente
            let lastWeekTotal = 0;
            let lastWeekValid = 0;
            lastWeek.forEach(entry => {
                const scoreValue = parseFloat(entry.data.meanFlowQuality);
                if (!isNaN(scoreValue) && scoreValue > 0) {
                    lastWeekTotal += scoreValue;
                    lastWeekValid++;
                }
            });
            const lastWeekAverage = lastWeekValid > 0 ? lastWeekTotal / lastWeekValid : 0;
            
            // Calculer l'amélioration
            if (lastWeekAverage > 0) {
                weeklyImprovement = thisWeekAverage - lastWeekAverage;
            }
        }

        return {
            totalTime: `${hours}h ${minutes}m`,
            averageScore: Math.round(averageScore * 10) / 10,
            weeklyImprovement: Math.round(weeklyImprovement * 10) / 10
        };
    };

    const calculateCategoryStats = (flowData) => {
        const categoryCount = {};
        
        flowData.forEach(entry => {
            for (let i = 1; i <= 7; i++) {
                // Vérifier si les données sont dans le nouveau format ou l'ancien format
                const category = entry[`category_${i}`] || (entry.data ? entry.data[`category_${i}`] : '');
                if (category && category !== '') {
                    categoryCount[category] = (categoryCount[category] || 0) + 1;
                }
            }
        });

        return Object.entries(categoryCount)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 6);
    };

    const convertTimeToHours = (timeString) => {
        if (!timeString || timeString === '00:00') return 0;
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours + (minutes / 60);
    };



    // Fonctions de rendu modulaires
    const renderStatsCards = () => {
        if (!currentConfig.sections.statsCards.enabled) return null;

        const cards = [];
        const cardConfigs = currentConfig.sections.statsCards.cards;

        if (cardConfigs.flowTime.enabled) {
            cards.push({
                order: cardConfigs.flowTime.order,
                component: (
                    <div key="flowTime" className="stats-card">
                        <div className="stats-icon">
                            <i className="fas fa-font-awesome"></i>
                        </div>
                        <div className="stats-content">
                            <h3>Flow Time</h3>
                            <h2>{dashboardData.flowStats.totalTime}</h2>
                            <p className="stats-subtitle">This week</p>
                        </div>
                    </div>
                )
            });
        }

        if (cardConfigs.averageScore.enabled) {
            cards.push({
                order: cardConfigs.averageScore.order,
                component: (
                    <div key="averageScore" className="stats-card">
                        <div className="stats-icon">
                            <i className="fas fa-star"></i>
                        </div>
                        <div className="stats-content">
                            <h3>Average Score</h3>
                            <h2>{dashboardData.flowStats.averageScore}/5</h2>
                            <p className={`stats-subtitle ${dashboardData.flowStats.weeklyImprovement < 0 ? 'negative' : 'positive'}`}>{dashboardData.flowStats.weeklyImprovement} vs previous week</p>
                        </div>
                    </div>
                )
            });
        }

        if (cardConfigs.goals.enabled) {
            cards.push({
                order: cardConfigs.goals.order,
                component: (
                    <div key="goals" className="stats-card">
                        <div className="stats-icon">
                            <i className="fas fa-bullseye"></i>
                        </div>
                        <div className="stats-content">
                            <h3>Goals</h3>
                            <h2>{dashboardData.goals.active}</h2>
                            <p className="stats-subtitle">Completed fields</p>
                        </div>
                    </div>
                )
            });
        }

        if (cardConfigs.experiences.enabled) {
            cards.push({
                order: cardConfigs.experiences.order,
                component: (
                    <div key="experiences" className="stats-card">
                        <div className="stats-icon">
                            <i className="fas fa-book"></i>
                        </div>
                        <div className="stats-content">
                            <h3>Experiences</h3>
                            <h2>{dashboardData.experiences.total}</h2>
                            <p className="stats-subtitle">Total recorded</p>
                        </div>
                    </div>
                )
            });
        }

        return (
            <div className="dashboard-header">
                {cards.sort((a, b) => a.order - b.order).map(card => card.component)}
            </div>
        );
    };

    const renderTimerAndNotes = () => {
        if (!currentConfig.sections.focusTimer?.enabled) return null;
        
        return (
            <div className="dashboard-section timer-notes-section">
                <div className="row g-4">
                    <div className="col-lg-6">
                        <FocusTimer user={user} />
                    </div>
                    <div className="col-lg-6">
                        <PersonalNotes />
                    </div>
                </div>
            </div>
        );
    };

    const renderCharts = () => {
        if (!currentConfig.sections.charts.enabled) return null;

        const charts = [];
        const chartConfigs = currentConfig.sections.charts.charts;

        if (chartConfigs.flowEvolution.enabled) {
            charts.push({
                order: chartConfigs.flowEvolution.order,
                component: (
                    <div key="flowEvolution" className="chart-container">
                        <h3>Flow Evolution (Last 7 Days)</h3>
                        <div className="simple-chart">
                            <div className="chart-bars">
                                {dashboardData.flowData.slice(-7).map((data, index) => (
                                    <div key={index} className="chart-bar-group">
                                        <div className="chart-bar-label">{data.date}</div>
                                        <div className="chart-bars-container">
                                            <div 
                                                className="chart-bar score-bar" 
                                                style={{height: `${(data.score / 5) * 100}%`}}
                                                title={`Score: ${data.score}/5`}
                                            ></div>
                                            <div 
                                                className="chart-bar duration-bar" 
                                                style={{height: `${Math.min(data.duration * 10, 100)}%`}}
                                                title={`Duration: ${data.duration}h`}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="chart-legend">
                                <div className="legend-item">
                                    <div className="legend-color score-color"></div>
                                    <span>Flow Score</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color duration-color"></div>
                                    <span>Duration (h)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            });
        }

        if (chartConfigs.categoryDistribution.enabled) {
            charts.push({
                order: chartConfigs.categoryDistribution.order,
                component: (
                    <div key="categoryDistribution" className="chart-container">
                        <h3>Category Distribution</h3>
                        <div className="recharts-container">
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        dataKey="value"
                                        data={dashboardData.categoryData}
                                        nameKey="name"
                                        outerRadius={80}
                                        fill="#8884d8"
                                    >
                                        {dashboardData.categoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={getCategoryColor(entry.name)} />
                                        ))}
                                    </Pie>
                                    <Legend />
                                    <Tooltip
                                        formatter={(value, name) => {
                                            // Calcul du pourcentage
                                            const totalValue = dashboardData.categoryData.reduce((total, entry) => total + entry.value, 0);
                                            const percentage = totalValue !== 0 ? ((value / totalValue) * 100).toFixed(2) : 0;

                                            // Conversion de la valeur en hh:mm
                                            const formattedTime = convertDecimalToHHMM(value);

                                            return [`${formattedTime}h (${percentage}%)`, name];
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )
            });
        }

        return (
            <div className="dashboard-charts">
                {charts.sort((a, b) => a.order - b.order).map(chart => chart.component)}
            </div>
        );
    };

    const renderQuickAccess = () => {
        if (!currentConfig.sections.quickAccess.enabled) return null;

        const enabledButtons = navigationButtons
            .filter(button => currentConfig.sections.quickAccess.buttons[button.key]?.enabled)
            .map(button => ({
                ...button,
                order: currentConfig.sections.quickAccess.buttons[button.key].order
            }))
            .sort((a, b) => a.order - b.order);

        return (
            <div className="quick-access">
                <h3><i className="fas fa-rocket"></i> Quick Access</h3>
                <div className="buttons-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', }}>
                    {enabledButtons.map((button, index) => (
                        <button
                            key={index}
                            className="nav-button gradient-button"
                            onClick={() => navigate(button.path)}
                            style={{ 
                                '--button-gradient': button.gradient,
                                '--button-color': button.color 
                            }}
                        >
                            <div className="button-icon">
                                <i className={button.icon}></i>
                            </div>
                            <span className="button-label">{button.label}</span>
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    const renderActivities = () => {
        if (!currentConfig.sections.activities.enabled) return null;

        const components = [];
        const activityConfigs = currentConfig.sections.activities.components;

        if (activityConfigs.recentActivities.enabled) {
            components.push({
                order: activityConfigs.recentActivities.order,
                component: (
                    <div key="recentActivities" className="recent-activities">
                        <h3>Recent Activities (idea for Jason)</h3>
                        <div className="activity-list">
                            {dashboardData.recentActivities.map(activity => (
                                <div key={activity.id} className="activity-item">
                                    <div className="activity-icon">
                                        <i className={activity.icon}></i>
                                    </div>
                                    <div className="activity-content">
                                        <p className="activity-title">{activity.title}</p>
                                        <p className="activity-time">{activity.time}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            });
        }

        if (activityConfigs.upcomingDeadlines.enabled) {
            components.push({
                order: activityConfigs.upcomingDeadlines.order,
                component: (
                    <div key="upcomingDeadlines" className="upcoming-deadlines">
                        <h3>Upcoming Deadlines (idea for Jason)</h3>
                        <div className="deadline-list">
                            {dashboardData.upcomingDeadlines.map(deadline => (
                                <div key={deadline.id} className="deadline-item">
                                    <div className="deadline-date">{deadline.date}</div>
                                    <div className="deadline-content">
                                        <p className="deadline-title">{deadline.title}</p>
                                        <div className="progress">
                                            <div 
                                                className="progress-bar" 
                                                style={{width: `${deadline.progress}%`}}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            });
        }

        return (
            <div className="dashboard-bottom">
                {components.sort((a, b) => a.order - b.order).map(comp => comp.component)}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Rendu principal avec système modulaire
    const renderDashboardSections = () => {
        const sections = [];
        
        // Collecte des sections activées avec leur ordre
        Object.entries(currentConfig.sections).forEach(([sectionKey, sectionConfig]) => {
            if (sectionConfig.enabled) {
                let sectionComponent = null;
                
                switch (sectionKey) {
                    case 'statsCards':
                        sectionComponent = renderStatsCards();
                        break;
                    case 'focusTimer':
                        sectionComponent = renderTimerAndNotes();
                        break;
                    case 'charts':
                        sectionComponent = renderCharts();
                        break;
                    case 'quickAccess':
                        sectionComponent = renderQuickAccess();
                        break;
                    case 'activities':
                        sectionComponent = renderActivities();
                        break;
                    default:
                        break;
                }
                
                if (sectionComponent) {
                    sections.push({
                        order: sectionConfig.order || 999,
                        component: sectionComponent
                    });
                }
            }
        });
        
        // Tri des sections par ordre et rendu
        return sections
            .sort((a, b) => a.order - b.order)
            .map((section, index) => (
                <React.Fragment key={index}>
                    {section.component}
                </React.Fragment>
            ));
    };

    return (
        <div>
            {planValidity === 2 ? (
                <div>
                    <DashBoardNavBar userName={userName} boldItem={'overview'} />
                    <div className="overview-dashboard">
                        {/* Bouton de paramètres */}
                        <div className="dashboard-settings-button">
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => setShowSettings(true)}
                                title="Dashboard Settings"
                            >
                                <i className="fas fa-cog"></i>
                                <span className="ms-2">Customize Dashboard</span>
                            </button>
                        </div>
                        
                        {renderDashboardSections()}
                    </div>
                    
                    {/* Modal de paramètres */}
                    <DashboardSettings
                        show={showSettings}
                        onHide={() => setShowSettings(false)}
                        user={user}
                        currentConfig={currentConfig}
                        onConfigChange={handleConfigChange}
                    />
                </div>
            ) : (
                <NoSubscription />
            )}
        </div>
    );
};

export default OverviewDashboard;
